/**
 * Kaleidoscope URL Mode
 * 
 * Displays a palindrome pattern in the URL hash that creates a kaleidoscope effect.
 */

export interface KaleidoscopeState {
  pattern: string;
  offset: number;
  length: number;
}

/**
 * Initialize kaleidoscope state
 */
export function initializeKaleidoscope(): KaleidoscopeState {
  const length = 30; // Half the palindrome length (will be doubled to 60)
  // ASCII and basic box-drawing characters that render consistently across systems
  // Avoiding emojis and complex Unicode that may render differently
  const unusualChars = '╱╲│─╭╮╯╰├┤┬┴┼┌┐└┘╔╗╚╝║═╤╧╪╫╬▀▄▌▐░▒▓▁▂▃▄▅▆▇█▉▊▋▌▍▎▏▐▔▕■□▲△▼▽◆◇○●◐◑◒◓◔◕◖◗◘◙◚◛◜◝◞◟◠◡';

  // Generate random pattern for left half
  const leftHalf: string[] = [];
  for (let i = 0; i < length; i++) {
    const charIndex = Math.floor(Math.random() * unusualChars.length);
    leftHalf.push(unusualChars[charIndex]);
  }

  const pattern = leftHalf.join('');

  return {
    pattern,
    offset: 0,
    length,
  };
}

/**
 * Cycle kaleidoscope pattern (shift left half and mirror)
 */
export function cycleKaleidoscope(state: KaleidoscopeState): void {
  // Shift the pattern left by one character (wraps around)
  const { pattern, length } = state;
  const shifted = pattern.slice(1) + pattern[0];

  state.pattern = shifted;
  state.offset = (state.offset + 1) % length;
}

/**
 * Generate kaleidoscope palindrome string
 */
export function generateKaleidoscopeString(state: KaleidoscopeState): string {
  const { pattern } = state;
  // Create palindrome: left half + reversed left half
  const reversed = pattern.split('').reverse().join('');
  return pattern + reversed;
}

